import React, {
  forwardRef,
  useId,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import clsx from "clsx";
import DatePicker from "react-datepicker";
import { CalendarDays, X } from "lucide-react";

import "react-datepicker/dist/react-datepicker.css";
import {
  V2_DATEPICKER_PORTAL_ID,
  ensureV2DatepickerPortal,
} from "../date-picker/date-picker.tsx";
import styles from "./date-range.module.css";

/**
 * Props react-datepicker merges onto `customInput` via `cloneElement` (see library `renderDateInput`).
 * We only forward these to the real `<button>` so nothing else (e.g. a stray `ref` string) hits the DOM.
 */
const DATE_PICKER_INPUT_PASSTHROUGH_KEYS = [
  "onBlur",
  "onChange",
  "onClick",
  "onFocus",
  "onKeyDown",
  "name",
  "form",
  "autoFocus",
  "placeholder",
  "autoComplete",
  "title",
  "readOnly",
  "required",
  "tabIndex",
  "aria-describedby",
  "aria-invalid",
  "aria-labelledby",
  "aria-required",
] as const;

function passthroughPickerInputProps(
  rest: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of DATE_PICKER_INPUT_PASSTHROUGH_KEYS) {
    if (rest[k] !== undefined) {
      out[k] = rest[k];
    }
  }
  return out;
}

/** Same contract as `DatePickerFieldTrigger`: `react-datepicker` merges props via `cloneElement`. */
type DateRangeTriggerButtonProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "type" | "children"
> & {
  placeholderText: string;
};

const DateRangeTriggerButton = forwardRef<
  HTMLButtonElement,
  DateRangeTriggerButtonProps
>(function DateRangeTriggerButton(
  { placeholderText, className, value, disabled, id, ...rest },
  ref,
) {
    const hasValue = value != null && value !== "";

    return (
      <button
        ref={ref}
        type="button"
        id={id}
        disabled={disabled}
        className={clsx(styles.trigger, className)}
        {...passthroughPickerInputProps(rest as Record<string, unknown>)}
      >
        <CalendarDays
          size={18}
          strokeWidth={2}
          className={styles.triggerIcon}
          aria-hidden
        />
        <span
          className={clsx(
            styles.triggerLabel,
            !hasValue && styles.triggerLabelPlaceholder
          )}
        >
          {hasValue ? value : placeholderText}
        </span>
      </button>
    );
  }
);

DateRangeTriggerButton.displayName = "DateRangeTriggerButton";

export interface DateRangeProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (dates: [Date | null, Date | null]) => void;
  /** Stable id for `htmlFor` when the label is rendered outside this component. */
  id?: string;
  label?: string;
  disabled?: boolean;
  /** When true, renders the calendar inline instead of in a popper. Default false. */
  inline?: boolean;
  placeholderText?: string;
  /**
   * Show a clear control when both start and end are set. Clears the range via `onChange([null, null])`.
   * Default true.
   */
  clearable?: boolean;
  minDate?: Date;
  maxDate?: Date;
  /** Optional predicate used to enable or disable individual calendar dates. */
  filterDate?: (date: Date) => boolean;
  /** Override how the chosen range is shown in the trigger (default: `toLocaleDateString()`). */
  formatDisplayDate?: (d: Date) => string;
  /** `react-datepicker` `dateFormat` for calendar + parsing. Default `MMM d, yyyy`. */
  pickerDateFormat?: string;
  /**
   * Uses `withPortal` instead of the default popper layout. Prefer this inside v2 `Modal`
   * (and similar) to avoid the library spreading `DatePicker` props onto a function-component
   * popper wrapper, which can trigger strict-mode errors with legacy string refs.
   */
  withPortal?: boolean;
}

export function DateRange({
  startDate,
  endDate,
  onChange,
  id: idProp,
  label,
  disabled = false,
  inline = false,
  placeholderText = "Select Date Range",
  clearable = true,
  minDate,
  maxDate,
  filterDate,
  formatDisplayDate,
  pickerDateFormat = "MMM d, yyyy",
  withPortal = false,
}: DateRangeProps) {
  if (!inline) {
    ensureV2DatepickerPortal();
  }
  const reactId = useId().replace(/:/g, "");
  const fieldId = idProp ?? reactId;

  const fmt = formatDisplayDate ?? ((d: Date) => d.toLocaleDateString());
  const displayValue =
    startDate && endDate
      ? `${fmt(startDate)} - ${fmt(endDate)}`
      : startDate
        ? fmt(startDate)
        : "";

  const handleRangeChange = (
    dates: [Date | null, Date | null] | null
  ) => {
    if (dates == null) {
      onChange([null, null]);
      return;
    }
    onChange([dates[0] ?? null, dates[1] ?? null]);
  };

  const labelEl: ReactNode =
    label != null && label !== "" ? (
      <label
        className={clsx("sub-heading-md", styles.label)}
        htmlFor={fieldId}
      >
        {label}
      </label>
    ) : null;

  return (
    <div className={styles.root}>
      {labelEl}
      {inline ? (
        <DatePicker
          id={fieldId}
          selectsRange
          startDate={startDate ?? undefined}
          endDate={endDate ?? undefined}
          onChange={(dates) => handleRangeChange(dates)}
          disabled={disabled}
          inline
          minDate={minDate}
          maxDate={maxDate}
          filterDate={filterDate}
          calendarClassName={styles.calendar}
          dateFormat={pickerDateFormat}
        />
      ) : (
        <div className={styles.fieldRow}>
          <DatePicker
            id={fieldId}
            selectsRange
            startDate={startDate ?? undefined}
            endDate={endDate ?? undefined}
            onChange={(dates) => handleRangeChange(dates)}
            disabled={disabled}
            withPortal={withPortal}
            wrapperClassName={clsx(styles.inputWrapper, "body-sm")}
            calendarClassName={styles.calendar}
            popperClassName={styles.popper}
            popperPlacement="bottom-start"
            portalId={V2_DATEPICKER_PORTAL_ID}
            popperProps={{ strategy: "fixed" }}
            showPopperArrow={false}
            dateFormat={pickerDateFormat}
            placeholderText={placeholderText}
            minDate={minDate}
            maxDate={maxDate}
            filterDate={filterDate}
            customInput={
              <DateRangeTriggerButton
                id={fieldId}
                placeholderText={placeholderText}
                value={displayValue}
                disabled={disabled}
              />
            }
          />
          {clearable && startDate && endDate && !disabled ? (
            <button
              type="button"
              className={styles.clearButton}
              aria-label="Clear date range"
              onClick={(e) => {
                e.stopPropagation();
                onChange([null, null]);
              }}
            >
              <X size={16} strokeWidth={2} aria-hidden />
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
